import { flowStore } from '@/entities/flow'
import { readNodeCustomDataSnapshot } from '@/entities/node'
import type { NodeContextPayload } from '@/entities/node/lib'
import {
  collectGroupDescendantNodes,
  isGroupContainerNode,
  isListNode,
  resolveNodeContextPayloadsForNode,
} from '@/entities/node/lib'
import { useProjectId } from '@/entities/project'
import {
  handleSubmitSucceed,
  requestAISubmitTask,
  requestAISubmitTaskGroup,
} from '@/entities/task'
import { calculatePointConsume } from '@/entities/task/lib/point-rule-calculator'
import { pointRuleConfigStore } from '@/entities/task/model/point-rule-config-store'
import { ensureAiWatermarkReadyForImageSubmit, openPointInsufficientDialog } from '@/entities/team'
import type { ApiResponse } from '@/shared/api'
import { requestConfirmDialog } from '@/shared/components/ui/confirm-dialog-command'
import { getFlowPermission } from '@/shared/config/flow-permission-store'
import { openMemberUpgradeDialog } from '@/shared/lib/member-upgrade-store'
import type { BaseNodeData } from '@/shared/lib/node-registry'
import {
  buildCreditInputForNode,
  getOp,
  resolveEstimatedItemCountForNode,
  type OpCreditInput,
} from '@/shared/lib/op-registry'
import SLog from '@/shared/lib/SLog'
import { hasRunningGlobalTask, markTaskInfoSubmitted } from '@/shared/lib/task/task-info-store'
import type { Edge, Node } from '@xyflow/react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

export interface SubmitOpTaskInput {
  nodeType?: string
  contextPayloads?: NodeContextPayload[]
}

export interface SubmitOpTaskCoreInput extends SubmitOpTaskInput {
  nodeId: string
  projectId: string
  source?: string
}

export interface SubmitCanvasNodeTaskInput {
  projectId: string
  nodeId: string
  nodes?: Node[]
  edges?: Edge[]
  nodeType?: string
  source?: string
}

export type SubmitTaskScope = 'single' | 'group' | 'workflow'
export type SubmitPreflightValidationMode = 'strict' | 'estimate-only'

export interface SubmitCanvasTaskInput {
  projectId: string
  rootNodeId: string
  scope: SubmitTaskScope
  nodes?: Node[]
  edges?: Edge[]
  nodeType?: string
  source?: string
  isContinue?: boolean
  runId?: string
  skipConfirm?: boolean
}

export interface SubmitTaskPreflightNode {
  nodeId: string
  nodeType: string
  nodeData: BaseNodeData
  contextPayloads: NodeContextPayload[]
  itemCount: number
  creditInput: OpCreditInput[]
}

export type SubmitTaskPreflightBuildResult =
  | {
    ok: true
    scope: SubmitTaskScope
    rootNodeId: string
    executableNodeIds: string[]
    chainCount: number
    nodeCount: number
    itemCount: number
    creditInput: OpCreditInput[]
    estimatedCredit: number
    estimatedImageCredit: number
    nodes: SubmitTaskPreflightNode[]
  }
  | { ok: false; message: string; invalidNodeId?: string }

export type SubmitOpTaskCoreResult =
  | { ok: true; runId: string }
  | { ok: false }


function normalizeCreditInput(input: OpCreditInput | OpCreditInput[] | null): OpCreditInput[] {
  if (!input) {
    return []
  }

  return Array.isArray(input) ? input : [input]
}

/**
 * 从 credit 构建结果中提取 task 信息（key 和可选的 model）。
 * creditInputs 为空时返回 null。
 */
function resolveSubmitTask(creditInputs: OpCreditInput[]): { key: string; model?: string } | null {
  if (creditInputs.length === 0) {
    return null
  }

  const key = creditInputs[0]!.key
  const model = creditInputs[0]!.data?.model as string | undefined

  return model ? { key, model } : { key }
}

function isRunningStatus(status: unknown) {
  return status === 'pending' || status === 'running'
}

function getNodeType(node: Node | undefined) {
  return typeof node?.type === 'string' ? node.type : ''
}

function isExecutableNode(node: Node | undefined) {
  const nodeType = getNodeType(node)
  if (!node || !nodeType || isGroupContainerNode(node)) {
    return false
  }

  const nodeData = readNodeCustomDataSnapshot<BaseNodeData>(node.id)
  const op = typeof nodeData.op === 'string' ? nodeData.op : ''

  if (op === 'director') {
    return false
  }

  const definition = op ? getOp(op) : undefined

  return Boolean(
    definition?.buildCreditInput
    && definition.targetNodeTypes.includes(nodeType),
  )
}

function collectWorkflowNodes({
  edges,
  nodes,
  rootNodeId,
}: {
  edges: Edge[]
  nodes: Node[]
  rootNodeId: string
}) {
  const nodesById = new Map(nodes.map((node) => [node.id, node]))
  const outgoing = new Map<string, string[]>()

  edges.forEach((edge) => {
    if (!nodesById.has(edge.source) || !nodesById.has(edge.target)) {
      return
    }
    outgoing.set(edge.source, [...(outgoing.get(edge.source) ?? []), edge.target])
  })

  const visited = new Set<string>()
  const visiting = new Set<string>()
  const reachableNodeIds: string[] = []
  let hasCycle = false

  const walk = (nodeId: string) => {
    if (visiting.has(nodeId)) {
      hasCycle = true
      return
    }
    if (visited.has(nodeId)) {
      return
    }

    visiting.add(nodeId)
    visited.add(nodeId)
    reachableNodeIds.push(nodeId)

    for (const nextNodeId of outgoing.get(nodeId) ?? []) {
      walk(nextNodeId)
    }

    visiting.delete(nodeId)
  }

  walk(rootNodeId)

  const memo = new Map<string, number>()
  const countPaths = (nodeId: string): number => {
    if (memo.has(nodeId)) {
      return memo.get(nodeId)!
    }

    const nextNodeIds = (outgoing.get(nodeId) ?? []).filter((nextNodeId) => visited.has(nextNodeId))
    if (nextNodeIds.length === 0) {
      memo.set(nodeId, 1)
      return 1
    }

    const count = nextNodeIds.reduce((total, nextNodeId) => total + countPaths(nextNodeId), 0)
    memo.set(nodeId, Math.max(1, count))
    return memo.get(nodeId)!
  }

  return {
    hasCycle,
    chainCount: hasCycle ? 0 : countPaths(rootNodeId),
    nodes: reachableNodeIds
      .map((nodeId) => nodesById.get(nodeId))
      .filter((node): node is Node => Boolean(node)),
  }
}

function collectSubmitTaskCandidates({
  edges,
  nodes,
  rootNodeId,
  scope,
}: {
  edges: Edge[]
  nodes: Node[]
  rootNodeId: string
  scope: SubmitTaskScope
}): { ok: true; candidates: Node[]; chainCount: number } | { ok: false; message: string } {
  const rootNode = nodes.find((node) => node.id === rootNodeId)
  if (!rootNode) {
    return { ok: false, message: '当前节点不存在' }
  }

  if (scope === 'single') {
    return { ok: true, candidates: [rootNode], chainCount: 1 }
  }

  if (scope === 'group') {
    const candidates = collectGroupDescendantNodes(rootNodeId, nodes).filter(isExecutableNode)
    return {
      ok: true,
      candidates,
      chainCount: Math.max(1, candidates.length),
    }
  }

  const workflow = collectWorkflowNodes({ edges, nodes, rootNodeId })
  if (workflow.hasCycle) {
    return { ok: false, message: '工作流存在循环连接，无法执行' }
  }

  return {
    ok: true,
    candidates: workflow.nodes.filter(isExecutableNode),
    chainCount: workflow.chainCount,
  }
}

function calculateEstimatedCredit(input: OpCreditInput[]) {
  if (pointRuleConfigStore.status !== 'ready') {
    return 0
  }

  return input.reduce((total, currentInput) => {
    return total + calculatePointConsume({
      payload: {
        key: currentInput.key,
        data: currentInput.data,
      },
      config: pointRuleConfigStore.pointRuleConfig,
    })
  }, 0)
}

function isImageCreditNode(nodeType: string) {
  return nodeType === 'image' || nodeType === 'gallery'
}

function calculateEstimatedImageCredit(nodes: SubmitTaskPreflightNode[]) {
  return calculateEstimatedCredit(
    nodes
      .filter((node) => isImageCreditNode(node.nodeType))
      .flatMap((node) => node.creditInput),
  )
}

function readUpstreamEstimatedItemCounts({
  contextPayloads,
  estimatedItemCounts,
  executableNodeIds,
}: {
  contextPayloads: NodeContextPayload[]
  estimatedItemCounts: ReadonlyMap<string, number>
  executableNodeIds: ReadonlySet<string>
}) {
  return contextPayloads
    .filter((payload) => executableNodeIds.has(payload.nodeId))
    .map((payload) => estimatedItemCounts.get(payload.nodeId))
    .filter((count): count is number => typeof count === 'number' && Number.isFinite(count) && count > 0)
}

function buildSubmitTaskEstimatePlan({
  candidates,
  contextPayloadsByNodeId,
  executableNodeIds,
}: {
  candidates: Node[]
  contextPayloadsByNodeId: ReadonlyMap<string, NodeContextPayload[]>
  executableNodeIds: ReadonlySet<string>
}) {
  const estimatedItemCounts = new Map<string, number>()
  const maxPassCount = Math.max(1, candidates.length)

  for (let passIndex = 0; passIndex < maxPassCount; passIndex += 1) {
    let changed = false

    for (const node of candidates) {
      const nodeType = getNodeType(node)
      if (!nodeType) continue

      const nodeData = readNodeCustomDataSnapshot<BaseNodeData>(node.id)
      const contextPayloads = contextPayloadsByNodeId.get(node.id) ?? []
      const upstreamItemCounts = readUpstreamEstimatedItemCounts({
        contextPayloads,
        estimatedItemCounts,
        executableNodeIds,
      })
      const count = resolveEstimatedItemCountForNode({
        contextPayloads,
        groupExecution: {
          executableNodeIds,
          estimatedItemCounts,
        },
        nodeData,
        nodeId: node.id,
        nodeType,
        upstreamItemCounts,
      })
      const normalizedCount = Math.max(1, Math.ceil(count))

      if (estimatedItemCounts.get(node.id) !== normalizedCount) {
        estimatedItemCounts.set(node.id, normalizedCount)
        changed = true
      }
    }

    if (!changed) break
  }

  return {
    estimatedItemCounts,
    orderedNodeIds: candidates.map((node) => node.id),
  }
}

function getBatchSubmitConfirmDescription(
  nodeCount: number,
  estimatedCredit: number,
  // estimatedImageCredit: number,
) {
  return `本次将按顺序执行 ${nodeCount} 个节点，预计消耗 ${estimatedCredit} 算力。关闭页面后任务仍会继续运行；你可以随时取消，尚未开始执行的节点不会产生算力消耗。是否开始执行？`
  // return `本次将按顺序执行 ${nodeCount} 个节点，预计消耗 ${estimatedCredit} 算力，其中图片专属算力预计消耗 ${estimatedImageCredit}。关闭页面后任务仍会继续运行；你可以随时取消，尚未开始执行的节点不会产生算力消耗。是否开始执行？`
}

function resolvePreflightNodeValidationMode({
  nodeId,
  rootNodeId,
  scope,
  validationMode,
}: {
  nodeId: string
  rootNodeId: string
  scope: SubmitTaskScope
  validationMode: SubmitPreflightValidationMode
}): SubmitPreflightValidationMode {
  if (scope === 'workflow' && nodeId === rootNodeId) {
    return 'strict'
  }

  return validationMode
}

export function buildSubmitTaskPreflight({
  edges,
  nodes,
  rootNodeId,
  scope,
  validationMode = scope === 'single' ? 'strict' : 'estimate-only',
}: {
  scope: SubmitTaskScope
  rootNodeId: string
  nodes: Node[]
  edges: Edge[]
  validationMode?: SubmitPreflightValidationMode
}): SubmitTaskPreflightBuildResult {
  const normalizedRootNodeId = rootNodeId.trim()
  if (!normalizedRootNodeId) {
    return { ok: false, message: '当前节点不存在' }
  }

  const rootNodeData = readNodeCustomDataSnapshot<BaseNodeData>(normalizedRootNodeId)
  if (isRunningStatus(rootNodeData.status)) {
    return { ok: false, message: '任务正在执行中', invalidNodeId: normalizedRootNodeId }
  }

  const candidateResult = collectSubmitTaskCandidates({
    edges,
    nodes,
    rootNodeId: normalizedRootNodeId,
    scope,
  })
  if (!candidateResult.ok) {
    return candidateResult
  }

  if (candidateResult.candidates.length === 0) {
    return {
      ok: false,
      message: scope === 'group' ? '组内没有可执行任务' : '当前节点没有可执行任务',
    }
  }

  // 生成 executableNodeIds
  // 这批节点会作为本轮任务范围。后面判断“某个上游是不是本轮会执行”就靠这个集合。
  const executableNodeIds = new Set(candidateResult.candidates.map((node) => node.id))

  //   先缓存每个节点的上下文
  // 对每个候选节点调用 resolveNodeContextPayloadsForNode，得到它的上游输入 payload，存到 contextPayloadsByNodeId。
  const contextPayloadsByNodeId = new Map<string, NodeContextPayload[]>()
  for (const node of candidateResult.candidates) {
    contextPayloadsByNodeId.set(
      node.id,
      resolveNodeContextPayloadsForNode({
        edges,
        nodeId: node.id,
        nodes,
      }).items,
    )
  }
  // 推导每个节点预计张数
  const estimatePlan = buildSubmitTaskEstimatePlan({
    candidates: candidateResult.candidates,
    contextPayloadsByNodeId,
    executableNodeIds,
  })
  const preflightNodes: SubmitTaskPreflightNode[] = []
  const creditInput: OpCreditInput[] = []
  let itemCount = 0

  for (const node of candidateResult.candidates) {
    const nodeType = getNodeType(node)
    const nodeData = readNodeCustomDataSnapshot<BaseNodeData>(node.id)
    const nodeValidationMode = resolvePreflightNodeValidationMode({
      nodeId: node.id,
      rootNodeId: normalizedRootNodeId,
      scope,
      validationMode,
    })

    if (!nodeType) {
      return { ok: false, message: '节点类型不能为空', invalidNodeId: node.id }
    }

    if (isRunningStatus(nodeData.status)) {
      return { ok: false, message: '任务正在执行中', invalidNodeId: node.id }
    }

    const contextPayloads = contextPayloadsByNodeId.get(node.id) ?? []

    const creditInputResult = buildCreditInputForNode({
      contextPayloads,
      groupExecution: {
        executableNodeIds,
        estimatedItemCounts: estimatePlan.estimatedItemCounts,
      },
      nodeData,
      nodeId: node.id,
      nodeType,
    })

    if (!creditInputResult.ok && nodeValidationMode === 'strict') {
      return { ok: false, message: creditInputResult.disabledMessage || '', invalidNodeId: node.id }
    }

    if (!creditInputResult.ok) {
      preflightNodes.push({
        contextPayloads,
        creditInput: [],
        itemCount: 0,
        nodeData,
        nodeId: node.id,
        nodeType,
      })
      continue
    }

    if (
      creditInputResult.submitDisabled
      && (nodeValidationMode === 'strict' || creditInputResult.enforceInBatch)
    ) {
      return {
        ok: false,
        message: creditInputResult.disabledMessage || '请补充任务参数后再提交',
        invalidNodeId: node.id,
      }
    }

    const nodeCreditInput = normalizeCreditInput(creditInputResult.input!)
    preflightNodes.push({
      contextPayloads,
      creditInput: nodeCreditInput,
      itemCount: creditInputResult.itemCount!,
      nodeData,
      nodeId: node.id,
      nodeType,
    })
    creditInput.push(...nodeCreditInput)
    itemCount += creditInputResult.itemCount!
  }

  return {
    ok: true,
    chainCount: candidateResult.chainCount,
    creditInput,
    estimatedCredit: calculateEstimatedCredit(creditInput),
    estimatedImageCredit: calculateEstimatedImageCredit(preflightNodes),
    executableNodeIds: candidateResult.candidates.map((node) => node.id),
    itemCount,
    nodeCount: candidateResult.candidates.length,
    nodes: preflightNodes,
    rootNodeId: normalizedRootNodeId,
    scope,
  }
}

export async function submitOpTaskCore(
  input: SubmitOpTaskCoreInput,
): Promise<SubmitOpTaskCoreResult> {
  const normalizedNodeId = input.nodeId.trim()
  const normalizedProjectId = input.projectId.trim()
  if (!normalizedNodeId || !normalizedProjectId) {
    return { ok: false }
  }

  const flowNode = flowStore.nodes[normalizedNodeId]
  const nodeType = (input.nodeType?.trim() || flowNode?.type || '').trim()
  if (!nodeType) {
    toast.error('节点类型不能为空')
    return { ok: false }
  }

  const nodeData = readNodeCustomDataSnapshot<BaseNodeData>(normalizedNodeId)

  if (nodeData.status === 'pending' || nodeData.status === 'running') {
    toast.error('任务正在执行中')
    return { ok: false }
  }

  try {
    const contextPayloads = input.contextPayloads
      ?? resolveNodeContextPayloadsForNode({
        edges: flowStore.edges as Edge[],
        nodeId: normalizedNodeId,
        nodes: Object.values(flowStore.nodes) as Node[],
      }).items

    const creditInput = buildCreditInputForNode({
      contextPayloads,
      nodeData,
      nodeId: normalizedNodeId,
      nodeType,
    })

    if (!creditInput.ok) {
      toast.error(creditInput.disabledMessage)
      return { ok: false }
    }

    if (creditInput.submitDisabled) {
      toast.error(creditInput.disabledMessage || '请补充任务参数后再提交')
      return { ok: false }
    }

    if (!getFlowPermission().isReadonly && nodeType === 'image' && ['collage', 'crop'].indexOf(nodeData.op || '') == -1) {
      const ready = await ensureAiWatermarkReadyForImageSubmit()
      if (!ready) {
        return { ok: false }
      }
    }

    const nodeCreditInput = normalizeCreditInput(creditInput.input!)
    const estimatedCredit = calculateEstimatedCredit(nodeCreditInput)
    const estimatedImageCredit = isImageCreditNode(nodeType) ? estimatedCredit : 0

    const taskInfo = resolveSubmitTask(nodeCreditInput)

    const is_list = isListNode(nodeType);
    const response = await requestAISubmitTask({
      project_id: normalizedProjectId,
      node_id: normalizedNodeId,
      is_list,
      point: estimatedCredit,
      image_point: estimatedImageCredit,
      ...taskInfo,
    })
    if (response.code !== 1) {
      SLog.logEvent('task_create', `fail msg-${response.msg} project_id-${normalizedProjectId} node_id-${normalizedNodeId} point-${estimatedCredit} op-${nodeData.op} node_type-${nodeType}`);
      handleSubmitError(response);
      return { ok: false }
    }
    SLog.logEvent('task_create', `suc project_id-${normalizedProjectId} node_id-${normalizedNodeId} point-${estimatedCredit} op-${nodeData.op} node_type-${nodeType} run_id-${response.data?.run_id}`);

    const runId = response.data?.run_id || ''

    handleSubmitSucceed(normalizedNodeId, runId)
    return { ok: true, runId }
  } catch (error) {
    toast.error(error instanceof Error && error.message ? error.message : '提交失败')
    return { ok: false }
  }
}

export async function submitCanvasTask({
  edges,
  isContinue = false,
  nodeType,
  nodes,
  projectId,
  rootNodeId,
  runId,
  scope,
  skipConfirm = false,
}: SubmitCanvasTaskInput): Promise<SubmitOpTaskCoreResult> {
  const normalizedProjectId = projectId.trim()
  const normalizedRootNodeId = rootNodeId.trim()
  if (!normalizedProjectId || !normalizedRootNodeId) {
    return { ok: false }
  }

  if ((scope === 'group' || scope === 'workflow') && hasRunningGlobalTask()) {
    toast.error('已有任务正在执行中')
    return { ok: false }
  }

  const flowNodes = (nodes ?? Object.values(flowStore.nodes)) as Node[]
  const flowEdges = edges ?? flowStore.edges
  const preflight = buildSubmitTaskPreflight({
    edges: flowEdges,
    nodes: flowNodes,
    rootNodeId: normalizedRootNodeId,
    scope,
    validationMode: scope === 'single' ? 'strict' : 'estimate-only',
  })

  if (!preflight.ok) {
    toast.error(preflight.message)
    return { ok: false }
  }

  if (preflight.nodes.some((node) => node.nodeType === 'image')) {
    const ready = await ensureAiWatermarkReadyForImageSubmit()
    if (!ready) {
      return { ok: false }
    }
  }


  if (scope !== 'single' && !isContinue && !skipConfirm) {
    const confirmed = await requestConfirmDialog({
      cancelText: '取消',
      confirmText: '开始执行',
      description: getBatchSubmitConfirmDescription(
        preflight.nodeCount,
        preflight.estimatedCredit,
        // preflight.estimatedImageCredit,
      ),
      title: '确认开始执行？',
    })

    if (!confirmed) {
      return { ok: false }
    }
  }

  try {
    if (scope === 'single') {
      const targetNode = flowNodes.find((node) => node.id === normalizedRootNodeId)
      const resolvedNodeType = (nodeType ?? targetNode?.type ?? preflight.nodes[0]?.nodeType ?? '').trim()
      const nodeData = readNodeCustomDataSnapshot<BaseNodeData>(normalizedRootNodeId)

      const taskInfo = resolveSubmitTask(preflight.nodes[0]?.creditInput ?? [])

      const is_list = isListNode(resolvedNodeType);
      const response = await requestAISubmitTask({
        project_id: normalizedProjectId,
        node_id: normalizedRootNodeId,
        is_list,
        point: preflight.estimatedCredit,
        image_point: preflight.estimatedImageCredit,
        ...taskInfo,
      })
      if (response.code !== 1) {
        SLog.logEvent('task_create', `fail msg-${response.msg} project_id-${normalizedProjectId} node_id-${normalizedRootNodeId} point-${preflight.estimatedCredit} node_type-${resolvedNodeType} op-${nodeData?.op}`);
        handleSubmitError(response);
        return { ok: false }
      }
      SLog.logEvent('task_create', `suc project_id-${normalizedProjectId} node_id-${normalizedRootNodeId} point-${preflight.estimatedCredit} node_type-${resolvedNodeType} op-${nodeData?.op}  run_id-${response.data?.run_id}`);

      const submittedRunId = response.data?.run_id || ''

      handleSubmitSucceed(normalizedRootNodeId, submittedRunId)
      return { ok: true, runId: submittedRunId }
    }

    const response = await requestAISubmitTaskGroup({
      project_id: normalizedProjectId,
      group_id: normalizedRootNodeId,
      is_continue: isContinue || undefined,
      run_id: runId?.trim() || undefined,
      point: preflight.estimatedCredit,
      image_point: preflight.estimatedImageCredit,
    })
    if (response.code !== 1) {
      SLog.logEvent('task_create_group', `fail msg-${response.msg} project_id-${normalizedProjectId} node_id-${normalizedRootNodeId} point-${preflight.estimatedCredit} group-${scope === 'group' ? 1 : 0}`);
      handleSubmitError(response);
      return { ok: false }
    }
    SLog.logEvent('task_create_group', `suc project_id-${normalizedProjectId} node_id-${normalizedRootNodeId} point-${preflight.estimatedCredit} group-${scope === 'group' ? 1 : 0}  run_id-${response.data?.run_id}`);

    const submittedRunId = response.data?.run_id || ''
    markTaskInfoSubmitted({
      isContinue,
      isGroup: scope === 'group',
      nodeId: normalizedRootNodeId,
      runId: submittedRunId,
      runMap: Object.fromEntries(preflight.executableNodeIds.map((nodeId) => [nodeId, 0])),
    })

    return { ok: true, runId: submittedRunId }
  } catch (error) {
    toast.error(error instanceof Error && error.message ? error.message : '提交失败')
    return { ok: false }
  }
}

export async function submitCanvasNodeTask({
  edges,
  nodeId,
  nodes,
  nodeType,
  projectId,
  source,
}: SubmitCanvasNodeTaskInput): Promise<SubmitOpTaskCoreResult> {
  return submitCanvasTask({
    edges,
    nodeType,
    nodes,
    projectId,
    rootNodeId: nodeId,
    scope: 'single',
    source,
  })
}

export function submitCanvasGroupTask(input: Omit<SubmitCanvasTaskInput, 'scope'>) {
  return submitCanvasTask({
    ...input,
    scope: 'group',
  })
}

export function submitCanvasWorkflowTask(input: Omit<SubmitCanvasTaskInput, 'scope'>) {
  return submitCanvasTask({
    ...input,
    scope: 'workflow',
  })
}

export function useSubmitOpTask({ nodeId }: { nodeId: string }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)
  const projectId = String(useProjectId() ?? '')
  const normalizedNodeId = nodeId.trim()
  const normalizedProjectId = projectId.trim()

  async function handleSubmitOpTask(
    input?: SubmitOpTaskInput,
  ): Promise<SubmitOpTaskCoreResult> {
    if (!normalizedNodeId || !normalizedProjectId || isSubmittingRef.current) {
      return { ok: false }
    }

    isSubmittingRef.current = true
    setIsSubmitting(true)
    try {
      return await submitOpTaskCore({
        ...input,
        nodeId: normalizedNodeId,
        projectId: normalizedProjectId,
      })
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  return {
    canSubmit: Boolean(normalizedNodeId && normalizedProjectId && !isSubmitting),
    isSubmitting,
    submitOpTask: handleSubmitOpTask,
  }
}


export function handleSubmitError(response: ApiResponse<any>) {
  if (response.code == -10) {
    // 通用算力不足
    openPointInsufficientDialog({ message: response.msg })
  } else if (response.code == -11) {
    // 图片算力不足
    openPointInsufficientDialog({ message: response.msg })
  } else if (response.code == -40 || response.code == -15 || response.code == -3) {
    // 没会员
    openMemberUpgradeDialog({ message: response.msg })
  } else {
    toast.error(response.msg || '提交失败')
  }
}
